import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { getProjectTags, indexProject, type TagSuggestion } from '../api/files'
import { getProject } from '../api/projects'
import Button from '../components/Button'
import TagInput from '../components/TagInput'
import { DOC_TYPE_HINTS, DOC_TYPE_LABELS, DOC_TYPES } from '../utils/docTypes'
import { formatFileSize } from '../utils/format'
import {
  createPromptUploadItems,
  type PromptUploadBatchSummary,
  type PromptUploadItem,
  uploadPromptBatch,
} from '../utils/promptUploadBatch'
import { canManageProject } from '../utils/rbac'
import {
  createUploadItems,
  type UploadBatchSummary,
  type UploadItem,
  uploadBatch,
} from '../utils/uploadBatch'

type UploadPhase = 'select' | 'uploading' | 'done'
type UploadMode = 'documents' | 'prompts'
const PROMPT_ACCEPT = '.md,.markdown'
const DOCUMENT_ACCEPT = '.pdf,.md,.txt,.html,.docx,.pptx,.json'

function statusIcon(status: UploadItem['status']): string {
  switch (status) {
    case 'complete':
      return '✓'
    case 'skipped':
      return '↷'
    case 'failed':
      return '✕'
    case 'uploading':
      return '↑'
    default:
      return '○'
  }
}

function statusColor(status: UploadItem['status']): string {
  switch (status) {
    case 'complete':
      return 'text-emerald-400 bg-emerald-500/20'
    case 'skipped':
      return 'text-amber-400 bg-amber-500/20'
    case 'failed':
      return 'text-red-400 bg-red-500/20'
    case 'uploading':
      return 'text-blue-400 bg-blue-500/20'
    default:
      return 'text-harbor-text-secondary bg-harbor-surface-raised'
  }
}

function statusLabel(status: UploadItem['status']): string {
  switch (status) {
    case 'pending':
      return 'Pending'
    case 'uploading':
      return 'Uploading'
    case 'complete':
      return 'Uploaded'
    case 'skipped':
      return 'Already exists'
    case 'failed':
      return 'Failed'
  }
}

export default function UploadPage() {
  const { id } = useParams<{ id: string }>()
  const projectId = Number(id)
  const navigate = useNavigate()

  const [uploadMode, setUploadMode] = useState<UploadMode>('documents')
  const [files, setFiles] = useState<File[]>([])
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([])
  const [phase, setPhase] = useState<UploadPhase>('select')
  const [summary, setSummary] = useState<UploadBatchSummary | null>(null)
  const [isStartingIndex, setIsStartingIndex] = useState(false)

  // Prompts mode has its own item/summary shape (no docType, no "skipped"
  // dedupe state, no follow-up indexing step) — kept separate rather than
  // forcing UploadItem/UploadBatchSummary to model a case they don't apply to.
  const [promptUploadItems, setPromptUploadItems] = useState<PromptUploadItem[]>([])
  const [promptSummary, setPromptSummary] = useState<PromptUploadBatchSummary | null>(null)

  // Document type is required before files can be added; it applies to the
  // whole batch, with a per-file override in the pending list. Changing it
  // must never clear an already-selected file list.
  const [docType, setDocType] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [tagSuggestions, setTagSuggestions] = useState<TagSuggestion[]>([])
  const [overrides, setOverrides] = useState<Record<string, string>>({})
  const [accessChecked, setAccessChecked] = useState(false)
  const [textbookPipelineEffective, setTextbookPipelineEffective] = useState(true)
  const [isDragActive, setIsDragActive] = useState(false)
  const dragCounter = useRef(0)

  const availableDocTypes = useMemo(
    () => (textbookPipelineEffective ? DOC_TYPES : DOC_TYPES.filter((type) => type !== 'textbook')),
    [textbookPipelineEffective]
  )

  useEffect(() => {
    let cancelled = false
    void getProject(projectId)
      .then((project) => {
        if (cancelled) return
        if (!canManageProject(project.projectRole)) {
          toast.error('You do not have permission to upload files to this project')
          navigate(`/projects/${projectId}`, { replace: true })
          return
        }
        const textbookOk = project.textbookPipelineEffective !== false
        setTextbookPipelineEffective(textbookOk)
        if (!textbookOk) {
          setDocType((prev) => (prev === 'textbook' ? '' : prev))
          setOverrides((prev) => {
            const next: Record<string, string> = {}
            for (const [key, value] of Object.entries(prev)) {
              if (value !== 'textbook') next[key] = value
            }
            return next
          })
        }
        setAccessChecked(true)
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Failed to load project')
          navigate('/projects', { replace: true })
        }
      })
    return () => {
      cancelled = true
    }
  }, [projectId, navigate])

  useEffect(() => {
    if (!accessChecked) return
    getProjectTags(projectId)
      .then(setTagSuggestions)
      .catch(() => {
        // Autocomplete is best-effort; typing new tags still works.
      })
  }, [projectId, accessChecked])

  const handleDocTypeChange = (nextDocType: string) => {
    if (!nextDocType || nextDocType === docType) return
    setDocType(nextDocType)
    // Drop overrides that now match the new batch default; keep real overrides.
    setOverrides((prev) => {
      const next: Record<string, string> = {}
      for (const [key, value] of Object.entries(prev)) {
        if (value !== nextDocType) next[key] = value
      }
      return next
    })
  }

  const replaceSelectedFiles = useCallback((nextFiles: File[]) => {
    if (nextFiles.length === 0) return
    setFiles(nextFiles)
    setUploadItems([])
    setSummary(null)
    setPromptUploadItems([])
    setPromptSummary(null)
    setPhase('select')
  }, [])

  const handleModeChange = (nextMode: UploadMode) => {
    if (nextMode === uploadMode) return
    setUploadMode(nextMode)
    // File accept differs by mode (Markdown-only for prompts) — drop any
    // selection made under the previous mode rather than silently uploading
    // files the new mode wouldn't have offered.
    setFiles([])
    setUploadItems([])
    setSummary(null)
    setPromptUploadItems([])
    setPromptSummary(null)
    setPhase('select')
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Ignore empty FileLists. Some browsers fire change with 0 files when the
    // picker is cancelled or the input is reset during an unrelated re-render;
    // that must not wipe an existing selection (e.g. after changing doc type).
    const list = e.target.files
    if (!list || list.length === 0) return
    replaceSelectedFiles(Array.from(list))
  }

  const canDrop = uploadMode === 'prompts' ? true : Boolean(docType)
  const dropAccept = uploadMode === 'prompts' ? PROMPT_ACCEPT : DOCUMENT_ACCEPT

  const resetDragState = useCallback(() => {
    dragCounter.current = 0
    setIsDragActive(false)
  }, [])

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!canDrop) return
      // Only count file drags; ignore text selections dragged over the box.
      if (!e.dataTransfer.types.includes('Files')) return
      dragCounter.current += 1
      setIsDragActive(true)
    },
    [canDrop]
  )

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!canDrop) return
      dragCounter.current -= 1
      if (dragCounter.current <= 0) resetDragState()
    },
    [canDrop, resetDragState]
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!canDrop) return
      e.dataTransfer.dropEffect = 'copy'
    },
    [canDrop]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      resetDragState()
      if (!canDrop) return
      const list = e.dataTransfer.files
      if (!list || list.length === 0) return
      const allFiles = Array.from(list)
      const extensions = dropAccept
        .split(',')
        .map((ext) => ext.trim().toLowerCase())
        .filter(Boolean)
      const accepted = allFiles.filter((file) =>
        extensions.some((ext) => file.name.toLowerCase().endsWith(ext))
      )
      const rejectedCount = allFiles.length - accepted.length
      if (rejectedCount > 0) {
        toast.error(`${rejectedCount} file(s) skipped — ${dropAccept} only`)
      }
      if (accepted.length === 0) return
      replaceSelectedFiles(accepted)
    },
    [canDrop, dropAccept, replaceSelectedFiles, resetDragState]
  )

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
    setUploadItems([])
    setSummary(null)
    setPromptUploadItems([])
    setPromptSummary(null)
    setPhase('select')
  }

  const progressCounts = useMemo(() => {
    const items = uploadMode === 'prompts' ? promptUploadItems : uploadItems
    if (items.length === 0) {
      return { done: 0, total: files.length, percent: 0 }
    }
    const done = items.filter(
      (item) => item.status === 'complete' || item.status === 'skipped' || item.status === 'failed'
    ).length
    const total = items.length
    return {
      done,
      total,
      percent: total > 0 ? Math.round((done / total) * 100) : 0,
    }
  }, [uploadMode, promptUploadItems, uploadItems, files.length])

  const canStartIndexing =
    uploadMode === 'documents' &&
    phase === 'done' &&
    summary !== null &&
    summary.failed === 0 &&
    summary.total > 0

  const canUpload = files.length > 0 && (uploadMode === 'prompts' ? true : Boolean(docType))

  const handleUpload = async () => {
    if (files.length === 0) return

    if (uploadMode === 'prompts') {
      const items = createPromptUploadItems(files)
      setPromptUploadItems(items)
      setPhase('uploading')
      setPromptSummary(null)

      const result = await uploadPromptBatch(items, {
        projectId,
        onItemUpdate: (item) => {
          setPromptUploadItems((prev) =>
            prev.map((existing) => (existing.key === item.key ? item : existing))
          )
        },
      })

      setPromptUploadItems(result.items)
      setPromptSummary(result.summary)
      setPhase('done')
      return
    }

    if (!docType) return

    const items = createUploadItems(files).map((item) => ({
      ...item,
      ...(overrides[item.key] ? { docType: overrides[item.key] } : {}),
    }))
    setUploadItems(items)
    setPhase('uploading')
    setSummary(null)

    const result = await uploadBatch(items, {
      projectId,
      docType,
      tags,
      onItemUpdate: (item) => {
        setUploadItems((prev) =>
          prev.map((existing) => (existing.key === item.key ? item : existing))
        )
      },
    })

    setUploadItems(result.items)
    setSummary(result.summary)
    setPhase('done')
  }

  const handleStartIndexing = async () => {
    setIsStartingIndex(true)
    try {
      await indexProject(projectId)
      navigate(`/projects/${projectId}`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start indexing')
      setIsStartingIndex(false)
    }
  }

  if (!accessChecked) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          className="text-harbor-text-secondary hover:text-harbor-text mb-2 inline-flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to project
        </button>
        <h1 className="text-2xl font-bold text-harbor-text">Upload Files</h1>
        <p className="text-harbor-text-secondary mt-1">
          {uploadMode === 'prompts'
            ? 'Upload Markdown prompt files into this project’s prompt library. They are not RAG-indexed.'
            : 'Upload documents to index for RAG search. Original files are kept on disk for re-download and updates.'}
        </p>
      </div>

      {phase === 'select' && (
        <div
          role="tablist"
          aria-label="Upload type"
          className="inline-flex rounded-lg border border-harbor-border bg-harbor-surface p-1"
        >
          <button
            type="button"
            role="tab"
            aria-selected={uploadMode === 'documents'}
            onClick={() => handleModeChange('documents')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              uploadMode === 'documents'
                ? 'bg-emerald-600 text-white'
                : 'text-harbor-text-secondary hover:text-harbor-text'
            }`}
          >
            Documents
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={uploadMode === 'prompts'}
            onClick={() => handleModeChange('prompts')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              uploadMode === 'prompts'
                ? 'bg-emerald-600 text-white'
                : 'text-harbor-text-secondary hover:text-harbor-text'
            }`}
          >
            Prompts (Markdown)
          </button>
        </div>
      )}

      {phase === 'select' && uploadMode === 'documents' && (
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                Document type <span className="text-red-400">*</span>
              </label>
              <select
                value={docType}
                onChange={(e) => handleDocTypeChange(e.target.value)}
                className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="" disabled>
                  Select a document type first…
                </option>
                {availableDocTypes.map((type) => (
                  <option key={type} value={type}>
                    {DOC_TYPE_LABELS[type]}
                  </option>
                ))}
              </select>
              <p className="text-xs text-harbor-text-muted mt-1">
                Applies to every file in this batch (per-file override below).
              </p>
              {docType && DOC_TYPE_HINTS[docType as keyof typeof DOC_TYPE_HINTS] && (
                <p className="text-xs text-emerald-400/80 mt-1 italic">
                  {DOC_TYPE_HINTS[docType as keyof typeof DOC_TYPE_HINTS]}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                Tags
              </label>
              <TagInput
                value={tags}
                onChange={setTags}
                suggestions={tagSuggestions}
                placeholder="e.g. copper, phase-2…"
              />
              <p className="text-xs text-harbor-text-muted mt-1">
                Optional. Tags are stamped on every chunk for filtered search.
              </p>
            </div>
          </div>
        </div>
      )}

      {phase === 'select' && uploadMode === 'prompts' && (
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6 space-y-2">
          <p className="text-sm text-harbor-text-secondary">
            Each file must be Markdown (<code className="font-mono text-emerald-400">.md</code>). An
            optional YAML frontmatter block sets the title and tags:
          </p>
          <pre className="rounded-lg border border-harbor-border bg-harbor-surface-raised/60 px-4 py-3 font-mono text-xs leading-6 text-harbor-text-secondary overflow-x-auto">
            {
              '---\ntitle: Release Checklist\ntags: [release, ops]\n---\n\nRun tests, tag release, publish notes.'
            }
          </pre>
          <p className="text-xs text-harbor-text-muted">
            Without frontmatter, the title falls back to the filename and the whole file becomes the
            prompt body. Uploading a title that already exists in this project updates it instead of
            creating a duplicate.
          </p>
        </div>
      )}

      {phase === 'select' && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          aria-disabled={!canDrop}
          data-testid="dropzone"
          data-drag-active={isDragActive && canDrop ? 'true' : 'false'}
          className={`bg-harbor-surface border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
            isDragActive && canDrop
              ? 'border-emerald-500 bg-emerald-500/10 scale-[1.01]'
              : canDrop
                ? 'border-harbor-border hover:border-emerald-500/50'
                : 'border-harbor-border opacity-60'
          }`}
        >
          <div className="text-5xl mb-4">📁</div>
          <h3 className="text-lg font-semibold text-harbor-text mb-2">
            {isDragActive && canDrop
              ? 'Release to add files'
              : canDrop
                ? 'Drop files here'
                : uploadMode === 'prompts'
                  ? 'Add Markdown prompt files'
                  : 'Select a document type to add files'}
          </h3>
          <p className="text-sm text-harbor-text-secondary mb-4">
            {isDragActive && canDrop
              ? 'Release to add to your selection'
              : canDrop
                ? 'Drag and drop files here, or click to browse files'
                : uploadMode === 'prompts'
                  ? 'Drag and drop, or click to browse — .md/.markdown only'
                  : 'The document type applies to the whole batch'}
          </p>
          <label
            className={`inline-flex items-center px-4 py-2 text-harbor-text text-sm font-medium rounded-lg transition-colors ${
              canDrop
                ? 'bg-emerald-600 hover:bg-emerald-700 cursor-pointer'
                : 'bg-harbor-surface-raised cursor-not-allowed'
            }`}
          >
            Browse Files
            <input
              type="file"
              multiple
              disabled={!canDrop}
              onChange={handleFileChange}
              className="hidden"
              accept={dropAccept}
            />
          </label>
          <p className="text-xs text-harbor-text-muted mt-3">
            {uploadMode === 'prompts'
              ? 'Supports Markdown (.md, .markdown) files only'
              : 'Supports PDF, Markdown, Text, HTML, DOCX, PPTX, and JSON files'}
          </p>
        </div>
      )}

      {files.length > 0 && (
        <div className="bg-harbor-surface border border-harbor-border rounded-xl">
          <div className="p-4 border-b border-harbor-border flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-harbor-text">
                {phase === 'done' ? 'Upload Results' : 'Selected Files'} ({files.length})
              </h3>
              {phase !== 'select' && (
                <p className="text-sm text-harbor-text-secondary mt-1">
                  {progressCounts.done}/{progressCounts.total} processed
                </p>
              )}
            </div>
            {phase === 'uploading' && (
              <span className="text-sm text-blue-400">Uploading batch…</span>
            )}
          </div>

          {phase !== 'select' && (
            <div className="px-4 pt-4">
              <div className="h-2 bg-harbor-surface-raised rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-300"
                  style={{ width: `${progressCounts.percent}%` }}
                />
              </div>
              {uploadMode === 'prompts' && promptSummary && (
                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  <span className="text-emerald-400">{promptSummary.complete} saved</span>
                  {promptSummary.failed > 0 && (
                    <span className="text-red-400">{promptSummary.failed} failed</span>
                  )}
                </div>
              )}
              {uploadMode === 'documents' && summary && (
                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  <span className="text-emerald-400">{summary.complete} uploaded</span>
                  {summary.skipped > 0 && (
                    <span className="text-amber-400">{summary.skipped} already existed</span>
                  )}
                  {summary.failed > 0 && (
                    <span className="text-red-400">{summary.failed} failed</span>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="divide-y divide-harbor-border max-h-96 overflow-y-auto">
            {(uploadMode === 'prompts'
              ? promptUploadItems.length > 0
                ? promptUploadItems
                : createPromptUploadItems(files)
              : uploadItems.length > 0
                ? uploadItems
                : createUploadItems(files)
            ).map((item, index) => (
              <div key={item.key} className="flex items-center justify-between p-4 gap-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div
                    className={`w-8 h-8 rounded flex items-center justify-center text-sm flex-shrink-0 ${statusColor(item.status)}`}
                  >
                    {item.status === 'uploading' ? (
                      <span className="animate-pulse">↑</span>
                    ) : (
                      statusIcon(item.status)
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-harbor-text truncate">
                      {item.file.name}
                    </p>
                    <p className="text-xs text-harbor-text-secondary">
                      {formatFileSize(item.file.size)}
                      {phase !== 'select' && <> · {statusLabel(item.status)}</>}
                    </p>
                    {item.error && item.status === 'failed' && (
                      <p className="text-xs text-red-400 mt-1 truncate">{item.error}</p>
                    )}
                    {item.status === 'skipped' && item.error && (
                      <p className="text-xs text-amber-400 mt-1 truncate">{item.error}</p>
                    )}
                  </div>
                </div>
                {phase === 'select' && uploadMode === 'documents' && (
                  <select
                    value={overrides[item.key] ?? docType}
                    onChange={(e) =>
                      setOverrides((prev) => {
                        const next = { ...prev }
                        if (e.target.value === docType) {
                          delete next[item.key]
                        } else {
                          next[item.key] = e.target.value
                        }
                        return next
                      })
                    }
                    title={`Document type for ${item.file.name}`}
                    className="px-2 py-1 bg-harbor-surface-raised border border-harbor-border rounded text-xs text-harbor-text-secondary focus:outline-none focus:ring-1 focus:ring-emerald-500 flex-shrink-0"
                  >
                    {availableDocTypes.map((type) => (
                      <option key={type} value={type}>
                        {DOC_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </select>
                )}
                {phase === 'select' && (
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 text-harbor-text-muted hover:text-red-400 rounded flex-shrink-0"
                    title={`Remove ${item.file.name}`}
                    aria-label={`Remove ${item.file.name}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-harbor-border flex flex-col sm:flex-row justify-end gap-3">
            {phase === 'select' && (
              <>
                <button
                  onClick={() => navigate(`/projects/${projectId}`)}
                  className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <Button onClick={handleUpload} disabled={!canUpload}>
                  {uploadMode === 'prompts' ? 'Save' : 'Upload'} {files.length} file(s)
                </Button>
              </>
            )}

            {phase === 'uploading' && (
              <Button disabled isLoading>
                {uploadMode === 'prompts' ? 'Saving' : 'Uploading'} {progressCounts.done}/
                {progressCounts.total}…
              </Button>
            )}

            {phase === 'done' && !canStartIndexing && (
              <>
                <button
                  onClick={() => {
                    setPhase('select')
                    setFiles([])
                    setUploadItems([])
                    setSummary(null)
                    setPromptUploadItems([])
                    setPromptSummary(null)
                  }}
                  className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
                >
                  Upload more
                </button>
                <Link
                  to={
                    uploadMode === 'prompts'
                      ? `/projects/${projectId}?tab=prompts`
                      : `/projects/${projectId}`
                  }
                  className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors text-center"
                >
                  {uploadMode === 'prompts' ? 'View prompts' : 'Back to project'}
                </Link>
              </>
            )}
          </div>
        </div>
      )}

      {canStartIndexing && (
        <div className="rounded-xl border-2 border-emerald-500 bg-emerald-500/10 p-6 shadow-lg shadow-emerald-500/10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-emerald-300">
                All files ready — index unindexed
              </h3>
              <p className="text-sm text-emerald-100/80 mt-1">
                {summary!.complete} new file(s) uploaded
                {summary!.skipped > 0 ? `, ${summary!.skipped} already in project` : ''}. Index them
                to make content searchable.
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to={`/projects/${projectId}`}
                state={{ highlightIndex: true }}
                className="px-4 py-2 text-sm text-emerald-200 hover:text-white hover:bg-emerald-500/20 rounded-lg transition-colors text-center"
              >
                Review files
              </Link>
              <Button
                size="lg"
                onClick={handleStartIndexing}
                isLoading={isStartingIndex}
                className="ring-2 ring-emerald-400 ring-offset-2 ring-offset-harbor-ring-offset animate-pulse"
              >
                Index unindexed
              </Button>
            </div>
          </div>
        </div>
      )}

      {phase === 'done' && uploadMode === 'documents' && summary && summary.failed > 0 && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4">
          <p className="text-sm text-red-300">
            {summary.failed} file(s) failed to upload. Fix the errors above and retry failed files,
            or return to the project to index the successful uploads.
          </p>
        </div>
      )}

      {phase === 'done' &&
        uploadMode === 'prompts' &&
        promptSummary &&
        promptSummary.failed > 0 && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4">
            <p className="text-sm text-red-300">
              {promptSummary.failed} file(s) failed — make sure each one is Markdown and not empty,
              then retry.
            </p>
          </div>
        )}
    </div>
  )
}
